import { useEffect, useState } from 'react'
import { CustomFilterIcon } from '@/components/ui/custom-filter-icon'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { AvailabilityMode, MonthFilter } from '@/types/ui/resource-availability'
import { useLanguage } from '@/contexts/language-context'
import { cn } from '@/lib/utils'

interface MonthFilterPopoverProps {
  monthLabel: string
  mode: AvailabilityMode
  filter: MonthFilter | undefined
  onChange: (filter: MonthFilter | undefined) => void
}

const VALUE_MIN = 0

function parseValidValue(value: string): number | null {
  if (value.trim() === '') return null
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return null
  // No upper bound: allocation can legitimately reach 200–300% on heavily overallocated
  // months, and an "above X%" filter needs to be able to express that.
  if (numeric < VALUE_MIN) return null
  return numeric
}

export function MonthFilterPopover({ monthLabel, mode, filter, onChange }: MonthFilterPopoverProps) {
  const [open, setOpen] = useState(false)
  const [op, setOp] = useState<MonthFilter['op']>(filter?.op ?? 'gte')
  const [value, setValue] = useState<string>(filter?.value !== undefined ? String(filter.value) : '')
  const { dictionary } = useLanguage()
  const t = dictionary?.resourceAvailability?.monthFilter

  // Re-sync the draft state whenever the popover opens or the parent filter changes.
  useEffect(() => {
    if (open) {
      setOp(filter?.op ?? 'gte')
      setValue(filter?.value !== undefined ? String(filter.value) : '')
    }
  }, [open, filter])

  const apply = () => {
    const numeric = parseValidValue(value)
    if (numeric === null) return
    onChange({ op, value: numeric })
    setOpen(false)
  }
  const clear = () => {
    onChange(undefined)
    setOpen(false)
  }
  const canApply = parseValidValue(value) !== null

  const isActive = !!filter
  const subtitle = mode === 'allocated'
    ? (t?.filterAllocSubtitle || 'Filter by allocation %')
    : (t?.filterAvailSubtitle || 'Filter by availability %')
  const triggerAria = (mode === 'allocated'
    ? (t?.filterAllocAriaLabel || 'Filter {month} allocation')
    : (t?.filterAvailAriaLabel || 'Filter {month} availability')
  ).replace('{month}', monthLabel)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={triggerAria}
          className={cn(
            'inline-flex items-center justify-center rounded p-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500',
            isActive ? 'text-blue-600 hover:text-blue-700' : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <CustomFilterIcon size={12} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-56 p-3">
        <div className="space-y-3">
          <div>
            <div className="text-xs font-semibold">{monthLabel}</div>
            <div className="text-[10px] text-muted-foreground">{subtitle}</div>
          </div>
          <div className="flex rounded-md border overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setOp('gte')}
              aria-pressed={op === 'gte'}
              className={cn(
                'flex-1 px-2 py-1 transition-colors',
                op === 'gte' ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-muted'
              )}
            >
              {t?.opAbove || '≥ above'}
            </button>
            <button
              type="button"
              onClick={() => setOp('lte')}
              aria-pressed={op === 'lte'}
              className={cn(
                'flex-1 px-2 py-1 border-l transition-colors',
                op === 'lte' ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-muted'
              )}
            >
              {t?.opBelow || '≤ below'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              inputMode="numeric"
              min={VALUE_MIN}
              step={5}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') apply()
              }}
              className="h-8 text-xs"
              placeholder={t?.valuePlaceholder || 'e.g. 75'}
            />
            <span className="text-xs text-muted-foreground">%</span>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={clear} disabled={!isActive}>
              {t?.clear || 'Clear'}
            </Button>
            <Button type="button" size="sm" onClick={apply} disabled={!canApply}>
              {t?.apply || 'Apply'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
